import type { ResolverTag } from '@ps/web-core-enums';
import type { ResolverOptions } from '@ps/web-shared-components';
import { ScreenSizeContext, useResolver } from '@ps/web-shared-components';
import { useCallback, useContext } from 'react';
import { useSelector } from 'react-redux';
import URL from 'url-parse';
import type { Primitive } from 'utility-types';

import { select } from '@swf/core/store';

export type Query = Record<string, Primitive>;

export interface BaseProps {
  resolverOptions?: ResolverOptions;
  query?: Query;
  openTab?: boolean;
  centered?: boolean;
  webTokenResolve?: boolean;
}

interface ResolverTagProps extends BaseProps {
  resolverTag: ResolverTag;
  targetUrl?: string;
}

interface TargetUrlProps extends BaseProps {
  resolverTag?: ResolverTag;
  targetUrl: string;
}

type Props = ResolverTagProps | TargetUrlProps;

const getWindowFeatures = (
  width: number,
  height: number,
  top: number,
  left: number,
  customWindowWidth = 800,
  customWindowHeight = 650
): string => {
  const customWindowTop = top + height / 2 - customWindowHeight / 2;
  const customWindowLeft = left + width / 2 - customWindowWidth / 2;

  return (
    `titlebar=no,resizable=no,` +
    `width=${customWindowWidth},height=${customWindowHeight},left=${customWindowLeft},top=${customWindowTop}`
  );
};

const useOpenWindow = ({
  query,
  openTab,
  resolverTag,
  centered,
  resolverOptions,
  targetUrl,
  webTokenResolve
}: Props) => {
  const resolvedUrl = useResolver(resolverTag, resolverOptions);
  const locale = useSelector(select.locale.get);
  const embedded = useSelector(select.device.isEmbedded);
  const isMobile = useSelector(select.device.isMobile);
  const {
    windowSize: { width, height },
    windowPosition: { top, left }
  } = useContext(ScreenSizeContext);

  const windowFeatures =
    (centered && !isMobile && getWindowFeatures(width, height, top, left)) ||
    'scrollbars=yes,resizable=no';

  const openWindow = useCallback(() => {
    const parsed = new URL(resolvedUrl, true);
    parsed.query.language = locale;
    Object.assign(parsed.query, query);
    const options = openTab ? ['_blank'] : [targetUrl || resolverTag, windowFeatures];
    const url = targetUrl || parsed.toString();

    return embedded
      ? targetUrl
        ? window.starsweb?.app.open?.(url, url)
        : window.starsweb?.app.execute(
            `${webTokenResolve ? 'webtoken-' : ''}resolve/${resolverTag}`
          )
      : window.open(url, ...options);
  }, [
    resolvedUrl,
    locale,
    query,
    openTab,
    resolverTag,
    windowFeatures,
    targetUrl,
    embedded,
    webTokenResolve
  ]);

  return openWindow;
};

export default useOpenWindow;
